import { Injectable, inject } from '@angular/core';
import {  ApiRequestOptions, ApiService } from '@services/api.service';
import { EndPoints } from '@services/EndPoints';

import { ClientGroupSummary, ClientSummary, GroupsQueryParams, ClientsQueryParams, ClientDto, ClientMiniDto } from '@dtos/clients/clients.dto';
import { ClientsGroupEntity } from '@entities/clients.entities';
import { DocumentsClientsDto } from '@dtos/clients/documents.dto';
import { BillingAccountItemDto, ClientBillingAccountDto } from '@dtos/clients/billingsAccounts.dto';
import { PriceRulesClientDto } from '@dtos/clients/priceRules.dtos';
import { ClientCertification } from '@dtos/clients/clientsCertifications.dto';
import { Helpers } from '@utils/helpers';
import { ErrorBase } from '@dtos/errors.dtos';
import { TransportPrincipalType } from '@enums/client.enum';
import { ProtocolDto } from '@dtos/clients/protocols.dto';
import { CertificationsDto } from '@dtos/certifications.dto';
import { Client, ISO_COUNTRIES, ISO_COUNTRIES_SELECT, TransportAddress } from '@dtos/index';
import { AdditionalServiceDto } from '@dtos/additional-services.dto';
import { ClientAdditionalServiceDto } from '@dtos/clients/clientAdditional-services.dto';

// DTOs para notificaciones de clientes
export interface ClientNotificationManagerDto {
    id: number;
    user_id: number;
    user_email: string;
    receive_notifications: boolean;
}

export interface ClientNotificationsDto {
    id: number;
    new_transport_notification: boolean;
    driver_assigned_notification: boolean;
    transport_finished_notification: boolean;
    cancelled_transport_notification: boolean;
    managers: ClientNotificationManagerDto[];
}

export interface UpdateClientNotificationsDto {
    new_transport_notification: boolean;
    driver_assigned_notification: boolean;
    transport_finished_notification: boolean;
    cancelled_transport_notification: boolean;
    managers: { user_id: number; receive_notifications: boolean }[];
}

// Interface para respuestas paginadas
export interface PaginatedResponse<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

@Injectable({ providedIn: 'root' })
export class ClientsProvider {

    private readonly api = inject(ApiService);

    private extractErrorMessages(error: any): string {
        const errBody = error?.error;
        if (errBody && typeof errBody === 'object') {
            const messages = [...new Set(Object.values<string[]>(errBody).flat())];
            return messages.join(' ');
        }
        if (typeof errBody === 'string') {
            return errBody;
        }
        return 'Error al guardar';
    }

    async getGroups(options : GroupsQueryParams): Promise<ClientGroupSummary[] | null> {
        try {
            options.page = options.page || 1;
            options.page_size = options.page_size || 50;
            return (await this.api.get<any>({ url: EndPoints.getClientGroups, queryParams: options }))["results"];
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }

    async getGroupsPaginated(options: GroupsQueryParams): Promise<PaginatedResponse<ClientGroupSummary> | null> {
        try {
            options.page = options.page || 1;
            options.page_size = options.page_size || 50;
            return await this.api.get<PaginatedResponse<ClientGroupSummary>>({ url: EndPoints.getClientGroups, queryParams: options });
        } catch (error) {
            console.error('Error al obtener grupos paginados:', error);
            return null;
        }
    }

    /**
     * OBTENER LISTA DE CLIENTES MINI PARA SELECTORES
     * @param options - PARÁMETROS OPCIONALES PARA LA CONSULTA (ej: not_subclients)
     * @returns ARRAY DE CLIENTES MINI CON ID Y NOMBRE
     */
    async getClientsMini(options?: { not_subclients?: boolean, parent?: number, can_create_transport?: boolean, client_group?: number }): Promise<ClientMiniDto[] | null> {
        try {
            let queryParams: any = { mini: true, ...options };

/*             if (options?.not_subclients) {
                queryParams.not_subclients = true;
            }
            if( options?.parent ) {
                queryParams.parent = options.parent;
            }
 */
            return await this.api.get<ClientMiniDto[]>({
                url: EndPoints.getClients,
                queryParams
            });
        } catch (error) {
            console.error('Error al obtener clientes mini:', error);
            return null;
        }
    }

    /**
     * OBTENER LISTA DE SUBENTIDADES DE UN CLIENTE PARA SELECTORES
     * @param parentId - ID DEL CLIENTE PADRE
     * @returns ARRAY DE SUBENTIDADES CON ID Y NOMBRE
     */
    async getSubentitiesByParent(parentId: number, can_create_transport?: boolean): Promise<ClientMiniDto[] | null> {
        try {
            let params: any  = { mini: true, parent: parentId };
            if(can_create_transport!= undefined){
                params.can_create_transport = can_create_transport;
            }
            return await this.api.get<ClientMiniDto[]>({
                url: EndPoints.getClients,
                queryParams: params
            });
        } catch (error) {
            console.error('Error al obtener subentidades:', error);
            return null;
        }
    }

    async saveGroup(body: any, id?: number ): Promise<any | null> {
        try {
            let result = null;
            if(id){
                result = await this.api.patch<any>({ url: EndPoints.updateClientGroup.replace("{clientGroupId}", id.toString()), body: body });
            } else {
                result = await this.api.post<any>({ url: EndPoints.createClientGroup, body: body });
            }
            return result;
        } catch (error) {
            console.error('Error al obtener admins:', error);
            throw error;
        }
    }
    
    async getGroupById(groupId : number | string): Promise<ClientsGroupEntity | null> {
        try {
            var result = await this.api.get<any>({ url: EndPoints.getClientGroup.replace("{clientGroupId}", groupId.toString()) });
            if(result.id){
                return  ClientsGroupEntity.fromDto({
                    id: result.id,
                    name: result.name,
                    country: result.country,
                    assigned_admins: result.assigned_admins ? result.assigned_admins.map((admin: any) => admin.id) : []
                });
            }
            return null;
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }

    async destroyGroup(groupId: number | string): Promise<boolean> {
        try {
            await this.api.delete<any>({ url: EndPoints.deleteClientGroup.replace("{clientGroupId}", groupId.toString()) });
            return true;
        } catch (error) {
            console.error('Error al eliminar grupo de cliente:', error);
            return false;
        }
    }

    async getClients(options : ClientsQueryParams): Promise<ClientSummary[] | null> {
        try {
            options.page = options.page || 1;
            options.page_size = options.page_size || 50;
            return (await this.api.get<any>({ url: EndPoints.getClients, queryParams: options }))["results"];
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }

    async getClientsPaginated(options: ClientsQueryParams): Promise<PaginatedResponse<ClientSummary> | null> {
        try {
            options.page = options.page || 1;
            options.page_size = options.page_size || 50;
            return await this.api.get<PaginatedResponse<ClientSummary>>({ url: EndPoints.getClients, queryParams: options });
        } catch (error) {
            console.error('Error al obtener clientes paginados:', error);
            return null;
        }
    }
    async deleteClientById(id: number | string): Promise<ClientDto | null> {
        try {
            return await this.api.delete<any>({ url: EndPoints.deleteClient.replace("{clientId}", id.toString()) });
        } catch (error) {
            console.error('Error al obtener admins:', error);
            throw error;
        }
    }

    async getClientGralData(id: number | string): Promise<ClientDto | null> {
        try {
            return await this.api.get<ClientDto>({ url: EndPoints.getClient.replace("{clientId}", id.toString()) });
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }

    async updateClientGralData(data: Partial<ClientDto>, file?: File | null | undefined): Promise<ClientDto | string> {
        try {
            const { id, logo, deleted_date, ...dto } = data;
            var options: ApiRequestOptions = { url: EndPoints.updateClient.replace("{clientId}", id!.toString()), formParams: dto };
            if (file) {
                options.fileParams = { 'logo': file };
            }
            return await this.api.patch<ClientDto>(options );
        } catch (error: any) {
            return this.extractErrorMessages(error);
        }
    }
    async createClientGralData(data: Partial<ClientDto>, file?: File | null | undefined): Promise<ClientDto | string> {
        try {
            const { id, logo, deleted_date, ...dto } = data;
            var options: ApiRequestOptions = { url: EndPoints.createClient.replace("{clientId}", id!.toString()), formParams: dto };
            if (file) {
                options.fileParams = { 'logo': file };
            }
            return await this.api.post<ClientDto>(options );
        } catch (error: any) {
            return this.extractErrorMessages(error);
        }
    }
    async getClientFullData(id: number | string): Promise<{
                    documents: DocumentsClientsDto[], 
                    billings: BillingAccountItemDto[], 
                    priceRules: PriceRulesClientDto, 
                    certifications: ClientCertification[], 
                    protocols: ProtocolDto[], 
                    certificationsTemplates: CertificationsDto[], 
                    additionalServiceTemplates: AdditionalServiceDto[]
                    additionalServices: ClientAdditionalServiceDto[]
                    transportAddress: TransportAddress[]
                
                } 
                    | null> {
        try {
           // var a = await this.api.get<any>({ url: EndPoints.getDocumentTemplates, queryParams: {client: id}  });
            let documents: DocumentsClientsDto[] = [];
            let billings: BillingAccountItemDto[] = [];
            let protocols: ProtocolDto[] = [];
            let certifications: ClientCertification[] = [];
            let certificationsTemplates: CertificationsDto[] = [];
            let additionalServiceTemplates: AdditionalServiceDto[] = [];
            let additionalServices: ClientAdditionalServiceDto[] = [];
            let transportAddress: TransportAddress[] = [];
            let priceRules: PriceRulesClientDto  = new PriceRulesClientDto();
            var getDocuments = async () => {
                try{
                    documents = await this.api.get<DocumentsClientsDto[]>({ url: EndPoints.getDocumentTemplates, queryParams: {client: id}  });
                }catch{}
            }
            var getBillings = async () => {
                try{
                    billings =await this.api.get<BillingAccountItemDto[]>({ url: EndPoints.getClientBillingAccounts, queryParams: {client: id}  })
                }catch{}
            }
            var getProtocols = async () => {
                try{
                    protocols= (await this.api.get<any>({ url: EndPoints.getProtocols, queryParams: {client_id: id, is_template: false}  }))["results"] as ProtocolDto[] ?? [];
                }catch{}
            }
            var getPriceRules = async () => {
                try{
                    priceRules = (await this.api.get<any>({ url: EndPoints.getPriceRule.replace("{scope}", "client").replace("{clientId}", `?client_id=${id.toString()}`)  }))["results"][0] ?? new PriceRulesClientDto();
                    priceRules.client = Number(id);
                }catch{}
            }
            var getCertifications = async () => { 
                try {
                    certifications = (await this.api.get<any>({ url: EndPoints.getClientCertifications, queryParams: {client: id}  }))["results"] as ClientCertification[] ?? [];                    
                } catch (error) {}
            }
            var getCertificationsTemplates = async () => { 
                try {
                    certificationsTemplates = (await this.api.get<any>({ url: EndPoints.getCertificationsTemlates}))["results"] as CertificationsDto[] ?? [];
                } catch (error) {}
            }
            var getAdditionalServiceTemplates = async () => { 
                try {
                    additionalServiceTemplates = (await this.api.get<AdditionalServiceDto[]>({ url: EndPoints.getClientAdditionalServicesTemplates}));
                } catch (error) {}
            }
            var getAdditionalServices = async () => { 
                try {
                    additionalServices = (await this.api.get<ClientAdditionalServiceDto[]>({ url: EndPoints.getClientAdditionalServices.replace("{clientId}", id.toString())  }));
                } catch (error) {}
            }
            var getTransportAddress = async () => { 
                try {
                    transportAddress = await this.getTransportAddressesByClient(id);
                } catch (error) {}
            }
            await getDocuments();
            await getBillings();
            await getProtocols();
            await getPriceRules();
            await getCertifications();
            await getCertificationsTemplates();
            await getAdditionalServiceTemplates();
            await getAdditionalServices();
            await getTransportAddress();

            
            return {
                documents,
                billings,
                priceRules,
                certifications, 
                protocols,
                certificationsTemplates,
                additionalServiceTemplates,
                additionalServices,
                transportAddress
            };

        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }
    async getTransportAddressesByClient(clientId: number | string): Promise<TransportAddress[] > {
            try {
                return await this.api.get<TransportAddress[]>({ url: EndPoints.getTransportAddresses, queryParams: {client_id: clientId}  });
            } catch (error) {
                return [];
            }
        }

    async createClientDocument(data: Partial<DocumentsClientsDto>, file?: File | null | undefined): Promise<DocumentsClientsDto | null> {
        try {
            const { id, ...dto } = data;
      
            var options: ApiRequestOptions = { url: EndPoints.createDocumentTemplate, formParams: dto };
            if (file) {
                options.fileParams = { 'file': file };
            }
            return await this.api.post<DocumentsClientsDto>(options );
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }   
    async deleteClientDocument(data: Partial<DocumentsClientsDto>): Promise<boolean> {
        try {
            const { id, ...dto } = data;
      
            var options: ApiRequestOptions = { url: EndPoints.deleteDocumentTemplate.replace("{templateId}", id!.toString())};
            await this.api.delete<DocumentsClientsDto>(options );
            return true;
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return false;
        }
    }
     async updateBilling(data: Partial<ClientBillingAccountDto>, id?: number): Promise<ClientBillingAccountDto | string> {
        try {
      
            var options: ApiRequestOptions = { url: EndPoints.createClientBillingAccount, body: data };
            if (id) {
                options.url = EndPoints.updateClientBillingAccount.replace("{accountId}", id.toString());
                return await this.api.patch<ClientBillingAccountDto>(options );
            }
            return await this.api.post<ClientBillingAccountDto>(options );
        } catch (error:any) {
            if(error["error"] !== undefined){
                return JSON.stringify(error["error"]);
            }
            console.error('Error al actualizar cuenta de facturación:', error);
            return JSON.stringify(error);
        }
    }      

    async deleteBillingById(id: number | string): Promise<ClientBillingAccountDto | null> {
        try {
            const result = await this.api.delete<any>({ url: EndPoints.deleteClientBillingAccount.replace("{accountId}", id.toString()) });

            return result;
        } catch (error) {
            console.error('Error al obtener admins:', error);
            throw error;
        }
    }    
    async getBillingById(id: number | string): Promise<ClientBillingAccountDto | null> {
        try {
            const result = await this.api.get<ClientBillingAccountDto>({ url: EndPoints.getClientBillingAccount.replace("{accountId}", id.toString()) });
            // TODO quitar cuando se resuelva el issue 20
            if(result &&  result.country && result.country.length >3 ){
                Object.entries(ISO_COUNTRIES_SELECT).find(([key, value]) => {
                    if((value.label).toUpperCase()  === result.country.toUpperCase()){
                        result.country = value.value;
                        return true;
                    }
                    return false;});
            }
            return result;
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }
    async importBillingAccountsExcel(file: File): Promise<{ created: number; errors_count: number; errors: string[] } | string> {
        try {
            return await this.api.post<{ created: number; errors_count: number; errors: string[] }>({
                url: EndPoints.importBillingAccountsExcel,
                fileParams: { file }
            });
        } catch (error: any) {
            return this.extractErrorMessages(error);
        }
    }

    async updatePriceRules(data: Partial<PriceRulesClientDto>): Promise<PriceRulesClientDto | ErrorBase> {
        try {

            var options: ApiRequestOptions = { url: EndPoints.createPriceRule.replace("{scope}", "client"), body: data };
            let result: PriceRulesClientDto; 
            if (!Helpers.isEmptyOrZero(data.id)) {
                options.url = EndPoints.updatePriceRule.replace("{scope}", "client").replace("{ruleId}", data.id!.toString());
                result = await this.api.patch<PriceRulesClientDto>(options );
            }else{
                result = await this.api.post<PriceRulesClientDto>(options );
            }
            return result;
        } catch (error:any) {
            console.error('Error al obtener admins:', error);
            return new ErrorBase(error, 500);
        }
    }
    async getProtocolsTemplates(type: TransportPrincipalType): Promise<ClientDto | null> {
        try {
            return await this.api.get<ClientDto>({ url: EndPoints.getProtocols, queryParams: { transport_principal_type: type, is_template: true } });
        } catch (error) {
            console.error('Error al obtener admins:', error);
            return null;
        }
    }
    async updateProtocol(data: Partial<ProtocolDto>): Promise<ProtocolDto | ErrorBase> {
        try {

            var options: ApiRequestOptions = { url: EndPoints.createProtocols, body: data };
            let result: PriceRulesClientDto; 
            if (!Helpers.isEmptyOrZero(data.id)) {
                options.url = EndPoints.createProtocols + data.id + '/';
                result = await this.api.patch<PriceRulesClientDto>(options );
            }else{
                result = await this.api.post<PriceRulesClientDto>(options );
            }
            return result;
        } catch (error:any) {
            console.error('Error al obtener admins:', error);
            return new ErrorBase(error, 500);
        }
    }

    async deleteProtocolById(protocolId: number): Promise<boolean> {
        try {
            await this.api.delete({ url: EndPoints.deleteProtocols.replace("{id}", protocolId.toString()) });
            return true;
        } catch (error) {
            console.error('Error al eliminar protocolo:', error);
            return false;
        }
    }

    async updateCertification(data: Partial<ClientCertification>): Promise<ClientCertification | ErrorBase> {
        try {

            var options: ApiRequestOptions = { url: EndPoints.updateClientCertification, body: data };
            let result: ClientCertification; 
            if(!Helpers.isEmptyOrZero(data.id)){
                options.url = EndPoints.updateClientCertification.replace("{certificationId}", data.id!.toString());
                result = await this.api.patch<ClientCertification>(options );
            }else{
                options.url = EndPoints.updateClientCertification.replace("{certificationId}/", "");
                result = await this.api.post<ClientCertification>(options );
            }
            
            return result;
        } catch (error:any) {
            console.error('Error al obtener admins:', error);
            return new ErrorBase(error, 500);
        }
    }    

    async deleteCertificationById(certificationId: number): Promise<boolean> {
        try {
            await this.api.delete({ url: EndPoints.deleteClientCertification.replace("{id}", certificationId.toString()) });
            return true;
        } catch (error) {
            console.error('Error al eliminar protocolo:', error);
            return false;
        }
    }
    async updateAdditionalServices(data: Partial<ClientAdditionalServiceDto>, id?: number): Promise<Partial<Partial<ClientAdditionalServiceDto>> | ErrorBase> {
        try {

            var options: ApiRequestOptions = { url: EndPoints.createClientAdditionalService, formParams: data };
            let result: ClientAdditionalServiceDto;
            if(!Helpers.isEmptyOrZero(id)){
                options.url = EndPoints.updateClientAdditionalService.replace("{serviceId}", id!.toString());
                result = await this.api.patch<ClientAdditionalServiceDto>(options );
            }else{

                //TODO falla
                result = await this.api.post<ClientAdditionalServiceDto>(options );
            }

            return result;
        } catch (error:any) {
            console.error('Error al obtener admins:', error);
            return new ErrorBase(error, 500);
        }
    }

    /**
     * OBTENER CONFIGURACIÓN DE NOTIFICACIONES DE UN CLIENTE
     * @param clientId - ID del cliente (entidad o subentidad)
     * @returns Configuración de notificaciones con managers
     */
    async getClientNotifications(clientId: number | string): Promise<ClientNotificationsDto | null> {
        try {
            return await this.api.get<ClientNotificationsDto>({
                url: EndPoints.getClientNotifications.replace("{clientId}", clientId.toString())
            });
        } catch (error) {
            console.error('Error al obtener notificaciones del cliente:', error);
            return null;
        }
    }

    /**
     * ACTUALIZAR CONFIGURACIÓN DE NOTIFICACIONES DE UN CLIENTE
     * @param clientId - ID del cliente (entidad o subentidad)
     * @param data - Datos de notificaciones a actualizar
     * @returns Configuración actualizada
     */
    async updateClientNotifications(clientId: number | string, data: UpdateClientNotificationsDto): Promise<ClientNotificationsDto | null> {
        try {
            return await this.api.put<ClientNotificationsDto>({
                url: EndPoints.updateClientNotifications.replace("{clientId}", clientId.toString()),
                body: data
            });
        } catch (error) {
            console.error('Error al actualizar notificaciones del cliente:', error);
            return null;
        }
    }

    /**
     * DESCARGA UN ARCHIVO CSV CON TODOS LOS CLIENTES
     * @returns BLOB DEL ARCHIVO CSV
     */
    async exportClientsCSV(): Promise<Blob | null> {
        try {
            const blob = await this.api.downloadFile({
                url: EndPoints.exportClientsCSV
            });

            // CREAR UN ENLACE TEMPORAL PARA DESCARGAR EL ARCHIVO
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'clients.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return blob;
        } catch (error) {
            console.error('Error al exportar clientes a CSV:', error);
            return null;
        }
    }

    /**
     * DESCARGA UN ARCHIVO CSV CON TODOS LOS GRUPOS DE CLIENTES
     * @returns BLOB DEL ARCHIVO CSV
     */
    async importClientsExcel(file: File): Promise<{ message: string } | string> {
        try {
            return await this.api.post<{ message: string }>({
                url: EndPoints.importClientsExcel,
                fileParams: { file }
            });
        } catch (error: any) {
            return this.extractErrorMessages(error);
        }
    }

    async exportClientGroupsCSV(): Promise<Blob | null> {
        try {
            const blob = await this.api.downloadFile({
                url: EndPoints.exportClientGroupsCSV
            });

            // CREAR UN ENLACE TEMPORAL PARA DESCARGAR EL ARCHIVO
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'client-groups.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return blob;
        } catch (error) {
            console.error('Error al exportar grupos de clientes a CSV:', error);
            return null;
        }
    }
}